import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef } from 'ag-grid-community';
import { Observable } from 'rxjs';
import { AgGridBaseComponent } from '../core/components/ag-grid-base.component';
import { DashboardReviewData } from '../core/models/dashboard-review-data.model';
import { Dashboard } from '../core/models/dashboard.model';
import { Placeholder } from '../core/models/placeholder.model';
import { PlaceholderPipe } from '../core/models/placeholder-pipe.model';
import { Review } from '../core/models/review.model';
import { Shipment } from '../core/models/shipment.model';
import { ShipmentPeriod } from '../core/models/shipment-period.model';
import { MomentPipe } from '../core/pipes/moment.pipe';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { DashboardService } from '../core/services/dashboard.service';
import { FileService } from '../core/services/file.service';
import { LocalizationService } from '../core/services/localization.service';
import { ShipmentService } from '../core/services/shipment.service';

@Component({
  selector: 'app-shipment',
  templateUrl: './shipment.component.html',
  styleUrls: ['./shipment.component.css']
})
export class ShipmentComponent extends AgGridBaseComponent implements OnInit {

  private static readonly REVENUE: string = 'revenue';
  private static readonly SHIPMENT: string = 'shipment';

  country: string;
  cpa: string;
  cpaName: string;
  currencyCode: string;
  from: number;
  to: number;
  productGroup: string;
  generatedOn: number;
  screenType: string;
  shipments: Observable<Shipment[]> | undefined;

  private fromDateFormatted: string;
  private toDateFormatted: string;
  private shipmentPeriods: ShipmentPeriod[];
  private currentShipmentPeriodIdx: number;

  private columnDefs: ColDef[] = [
    { field: 'account', filter: 'agTextColumnFilter', headerValueGetter: () => this.createHeader('T10') },
    { field: 'invoiceId', filter: 'agTextColumnFilter', headerValueGetter: () => this.createHeader('T11') },
    { field: 'invoiceDate', filter: 'agDateColumnFilter', headerValueGetter: () => this.createHeader('T12'), valueFormatter: this.formatDate.bind(this), width: 170 },
    { field: 'shipmentDate', filter: 'agDateColumnFilter', headerValueGetter: () => this.createHeader('T13'), valueFormatter: this.formatDate.bind(this), width: 170 },
    { field: 'grossUndiscountedCharge', filter: 'agNumberColumnFilter', type: 'numericColumn', headerValueGetter: () => this.createHeader('T14', this.placeholders), valueFormatter: this.formatDecimal },
    { field: 'adjustedDiscountedCharge', filter: 'agNumberColumnFilter', type: 'numericColumn', headerValueGetter: () => this.createHeader('T15', this.placeholders), valueFormatter: this.formatDecimal },
    { field: 'discountPercent', filter: 'agNumberColumnFilter', headerValueGetter: () => this.createHeader('T16'), type: 'rightAligned', valueFormatter: this.formatPercentage },
    { field: 'marketedProductId', filter: 'agTextColumnFilter', headerValueGetter: () => this.createHeader('T17') },
    { field: 'originCountry', filter: 'agTextColumnFilter', headerValueGetter: () => this.createHeader('T18'), width: 170 },
    { field: 'destinationCountry', filter: 'agTextColumnFilter', headerValueGetter: () => this.createHeader('T19'), width: 170 },
    { field: 'waybillNumber', filter: 'agTextColumnFilter', headerValueGetter: () => this.createHeader('T20') }
  ];

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected dashboardService: DashboardService,
    protected fileService: FileService,
    protected localizationService: LocalizationService,
    protected shipmentService: ShipmentService,
    protected momentPipe: MomentPipe,
    protected translatePipe: TranslatePipe
  ) {
    super(localizationService, momentPipe, translatePipe);

    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.country = this.activatedRoute.snapshot.queryParams.country;
    this.cpa = this.activatedRoute.snapshot.queryParams.cpa;
    this.from = +this.activatedRoute.snapshot.queryParams.from;
    this.to = +this.activatedRoute.snapshot.queryParams.to;
    this.productGroup = this.activatedRoute.snapshot.queryParams.productGroup;
    this.screenType = this.activatedRoute.snapshot.url[0].path;
  }

  get isPreviousButtonDisabled(): boolean {
    return !this.shipmentPeriods || this.currentShipmentPeriodIdx < 1;
  }

  get isNextButtonDisabled(): boolean {
    return this.shipmentPeriods ? this.currentShipmentPeriodIdx === this.shipmentPeriods.length - 1 : true;
  }

  get isRevenueScreen(): boolean {
    return this.screenType.toLowerCase() === ShipmentComponent.REVENUE;
  }

  get isShipmentScreen(): boolean {
    return this.screenType.toLowerCase() === ShipmentComponent.SHIPMENT;
  }

  get placeholders(): Placeholder[] {
    return [
      Placeholder.create('allRowCount', this.allRowCount, true),
      Placeholder.create('currencyCode', this.currencyCode),
      Placeholder.create('displayedRowCount', this.displayedRowCount, true),
      Placeholder.create('generatedOn', this.generatedOn, false, PlaceholderPipe.create('local-date', 'D MMM YYYY H:mm'))
    ];
  }

  ngOnInit(): void {
    const from: string | null = this.transformDate(this.from);
    const to: string | null = this.transformDate(this.to);

    if (from && to) {
      this.fromDateFormatted = from;
      this.toDateFormatted = to;
      this.dashboardService.getDashboardReviewData(this.cpa).subscribe((dashboardReviewData: DashboardReviewData) => {
        const dashboard = dashboardReviewData.getDashboards().find((dashboard: Dashboard) => this.productGroup === dashboard.getDiscountProductGroup())!;
        this.cpaName = dashboard.getCpaName() || '';
        this.currencyCode = dashboard.getCurrencyCode() || '';
        this.shipmentPeriods = this.getUniqueSortedShipmentPeriods(dashboard, dashboardReviewData.getReviews());
        this.currentShipmentPeriodIdx = this.getShipmentPeriodIdx();
        this.gridOptions?.api?.refreshHeader();
      });

      this.shipments = this.shipmentService.getShipments(this.cpa, this.fromDateFormatted, this.toDateFormatted);
      this.generatedOn = Date.now();
      this.initializeGridOptions(this.columnDefs);
    }
  }

  onExport() {
    this.shipmentService.exportShipments(this.cpa, this.fromDateFormatted, this.toDateFormatted).subscribe((file: File) => {
      this.fileService.saveFile(file);
    });
  }

  onGetPreviousPeriod() {
    if (this.shipmentPeriods?.length && this.currentShipmentPeriodIdx > 0) {
      this.navigateToPeriod(this.shipmentPeriods[this.currentShipmentPeriodIdx - 1]);
    }
  }

  onGetNextPeriod() {
    if (this.shipmentPeriods?.length && this.currentShipmentPeriodIdx < this.shipmentPeriods.length - 1) {
      this.navigateToPeriod(this.shipmentPeriods[this.currentShipmentPeriodIdx + 1]);
    }
  }

  private getUniqueSortedShipmentPeriods(dashboard: Dashboard, reviews: Review[]): ShipmentPeriod[] {
    const shipmentPeriods: ShipmentPeriod[] = reviews.filter((review: Review) => this.productGroup === review.getDiscountProductGroup())
      .map((review: Review) => this.createShipmentPeriod(review))
      .filter((shipmentPeriod): shipmentPeriod is ShipmentPeriod => shipmentPeriod !== null);

    const uniquePeriods: ShipmentPeriod[] = [];

    shipmentPeriods.forEach((period: ShipmentPeriod) => {
      if (!uniquePeriods.some((uniquePeriod: ShipmentPeriod) => period.isEqual(uniquePeriod))) {
        uniquePeriods.push(period);
      }
    });

    uniquePeriods.sort((a: ShipmentPeriod, b: ShipmentPeriod) => a.getStarDate() - b.getStarDate());

    if (this.screenType === ShipmentComponent.REVENUE) {
      const nextMeasurementStartDate: number | null = dashboard.getNextMeasurementStartDate();
      const nextMeasurementEndDate: number | null = dashboard.getNextMeasurementEndDate();
      if (nextMeasurementStartDate && nextMeasurementEndDate) {
        const shipmentPeriod: ShipmentPeriod = new ShipmentPeriod(nextMeasurementStartDate, nextMeasurementEndDate);

        if (!uniquePeriods.some((uniquePeriod: ShipmentPeriod) => shipmentPeriod.isEqual(uniquePeriod))) {
          uniquePeriods.push(shipmentPeriod);
        }
      }
    }

    return uniquePeriods;
  }

  private createShipmentPeriod(review: Review): ShipmentPeriod | null {
    let startDate: number | null;
    let endDate: number | null;

    switch (this.screenType) {
      case ShipmentComponent.REVENUE:
        startDate = review.getMeasurementStartDate();
        endDate = review.getMeasurementEndDate();
        break;

      case ShipmentComponent.SHIPMENT:
        startDate = review.getDiscountStartDate();
        endDate = review.getDiscountEndDate();
        break;

      default:
        return null;
    }

    if (startDate === null || endDate === null) {
      return null;
    }

    return new ShipmentPeriod(startDate, endDate);
  }

  private getShipmentPeriodIdx(): number {
    return this.shipmentPeriods
      .findIndex((shipmentPeriod: ShipmentPeriod) => this.from === shipmentPeriod.getStarDate() && this.to === shipmentPeriod.getEndDate());
  }

  private navigateToPeriod(period: ShipmentPeriod): void {
    this.router.navigate([], { queryParams: { from: period.getStarDate(), to: period.getEndDate() }, queryParamsHandling: 'merge' });
  }

  private transformDate(timestamp: number): string | null {
    return this.momentPipe.transform(timestamp, 'YYYY-MM-DD', this.locale, true);
  }

}
